<template>
  <div
    v-if="!loaded"
    class="flex h-full w-full items-center justify-center"
  >
    <wui-icon
      name="spinner"
      small
    />
  </div>
  <template v-else-if="iconType === IMAGE_TYPE && !isError">
    <div
      class="flex h-full w-full cursor-pointer items-center justify-center overflow-hidden"
      :class="[
        roundedClass,
        { 'is-black-box': isBlackBox, 'border border-bor-divider': bordered },
      ]"
      @click="onImageContainerClick"
    >
      <a
        :download="name"
        :href="fullUrl"
        class="h-full w-full"
      >
        <img
          class="h-full w-full shadow-all-0020-black-25"
          :class="objectFit === 'cover' ? 'object-cover max-w-none!' : 'object-contain'"
          data-test="file-image"
          :src="fullUrl"
          :alt="name"
          @error="onImageError"
        >
      </a>
      <wui-btn
        v-show="isBlackBox"
        class="absolute right-4 top-4 z-20 text-white hover:text-black-200!"
        prepend-icon="closeRegularMdM"
        icon
        :size="32"
      />
    </div>
  </template>
  <video-preview
    v-else-if="iconType === 'videoFile'"
    :src="url"
  />
  <a
    v-else
    class="relative flex h-full w-full items-center justify-center overflow-hidden"
    :class="roundedClass"
    :title="name"
    :download="name"
    :href="fullUrl"
    @contextmenu.prevent
    @click="onDocClick"
  >
    <wui-icon
      v-show="showIcon"
      class="size-full!"
      :name="iconName"
    />
  </a>
</template>

<script lang="ts" setup>
import { WuiBtn, WuiIcon } from '@wui/common-library'
import { computed, ref, watch } from 'vue'

import { VideoPreview } from '@/entities/video-preview'

import { openImageDialog } from '@/shared/lib/image-dialog-bridge'
import { getFileIconName, getFileTypeByName, IMAGE_TYPE } from '@/shared/utils/files'
import { tauriMode } from '@/shared/utils/import-meta'
import { getAuthenticatedMediaUrl } from '@/shared/utils/url'

const props = withDefaults(defineProps<{
  mime: string,
  cookie: string,
  name: string,
  isLoaded?: boolean,
  showIcon?: boolean,
  openPreviewOnClick?: boolean,
  objectFit?: 'contain' | 'cover',
  bordered?: boolean,
  roundedClass?: string,
}>(), {
  isLoaded: true,
  showIcon: true,
  openPreviewOnClick: true,
  objectFit: 'contain',
  bordered: true,
  roundedClass: 'rounded-8',
})

const emit = defineEmits<{
  (e: 'doc-click'): void
}>()

const onDocClick = (e: MouseEvent) => {
  const isTextFileByExtension = /\.(txt|log|ini|conf|cfg|yaml|yml|json|xml|csv|tsv|properties|env|gitignore)$/i.test(props.name)
  const isTextFileByMime = /^(text\/|application\/(json|xml|x-yaml|yaml|javascript))/i.test(props.mime)

  if (
    props.mime === 'application/pdf'
    || props.mime === 'text/markdown'
    || props.mime.includes('wordprocessingml')
    || props.mime.includes('spreadsheetml')
    || props.mime === 'application/msword'
    || props.mime === 'application/vnd.ms-excel'
    || props.mime === 'application/vnd.oasis.opendocument.spreadsheet'
    || props.mime === 'text/plain'
    || props.mime === 'application/json'
    || getFileTypeByName(props.name) === 'tableFile'
    || /\.md$/i.test(props.name)
    || /\.(docx|doc)$/i.test(props.name)
    || isTextFileByExtension
    || isTextFileByMime
  ) {
    e.stopPropagation()
    e.preventDefault()
    emit('doc-click')
  }
}
const isError = ref<boolean>(false)

const url = computed(() => `/files/${props.cookie}`)
const fullUrl = computed(() => getAuthenticatedMediaUrl(url.value))

const onImageError = () => {
  isError.value = true
}

const isBlackBox = ref<boolean>(false)

const onImgClick = () => {
  void openImageDialog({
    src: url.value,
    name: props.name,
    hasOverlay: true,
  })
}

const onImageContainerClick = (e: MouseEvent) => {
  if (!props.openPreviewOnClick) {
    return
  }
  e.preventDefault()
  e.stopPropagation()
  onImgClick()
}

const iconType = computed(() => getFileTypeByName(props.name))

const iconName = computed(() => getFileIconName(props.name))

const loaded = ref<boolean>(props.isLoaded)
watch(
  () => props.isLoaded,
  (v) => {
    if (v) {
    // Если запрашивать изображение сразу - 404
      setTimeout(() => {
        if (iconType.value === IMAGE_TYPE) {
          // Чтобы изображение не подгружалось ступенчато
          const image = new Image()
          image.onload = () => {
            loaded.value = v
          }
          image.onerror = () => {
            onImageError()

            loaded.value = v
          }
          if (tauriMode()) {
            image.src = fullUrl.value
          } else {
            image.src = url.value
          }
        } else {
          loaded.value = v
        }
      }, 1500)
    } else {
      loaded.value = v
    }
  })
</script>

<style scoped>
.is-black-box {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: auto;
  height: auto;
  background-color: var(--color-primary);
  z-index: 10;
}

img {
  max-width: fit-content;
}

</style>
